/**
 * ALT Enterprise Analytics Platform - Main App
 * Unified multi-domain dashboard with routing
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BusinessEntityProvider } from './contexts/BusinessEntityContext';
import ProtectedRoute from './components/ProtectedRoute';
import DomainProtectedRoute from './components/DomainProtectedRoute';
import DomainIndexRedirect from './components/DomainIndexRedirect';
import MainLayout from './layouts/MainLayout';

// Marketing layout and pages - lazy load for code splitting
const MarketingLayout = React.lazy(() => import('./domains/marketing/layouts/MarketingLayout'));
const OverviewPage = React.lazy(() => import('./domains/marketing/pages/OverviewPage'));
const MarketingPerformancePage = React.lazy(() => import('./domains/marketing/pages/MarketingPerformancePage'));
const CampaignsPage = React.lazy(() => import('./domains/marketing/pages/CampaignsPage'));
const WebTrafficPage = React.lazy(() => import('./domains/marketing/pages/WebTrafficPage'));
const MarketPage = React.lazy(() => import('./domains/marketing/pages/MarketPage'));
const CompetitorsPage = React.lazy(() => import('./domains/marketing/pages/CompetitorsPage'));
const TrendsPage = React.lazy(() => import('./domains/marketing/pages/TrendsPage'));
const PartnershipsPage = React.lazy(() => import('./domains/marketing/pages/PartnershipsPage'));
const MarketingDataModel = React.lazy(() => import('./domains/marketing/pages/DataModelPage'));
const MarketingDataManagementPage = React.lazy(() => import('./domains/marketing/pages/DataManagementPage'));
const OnlineReviewsPage = React.lazy(() => import('./domains/marketing/pages/OnlineReviewsPage'));
const AdvertisingPerformancePage = React.lazy(() => import('./domains/marketing/pages/AdvertisingPerformancePage'));
const MarketSharePage = React.lazy(() => import('./domains/marketing/pages/MarketSharePage'));
const CompletionGuaranteesPage = React.lazy(() => import('./domains/marketing/pages/CompletionGuaranteesPage'));
const UploadManagementPage = React.lazy(() => import('./domains/marketing/pages/UploadManagementPage'));
const MarketingDocumentationPage = React.lazy(() => import('./domains/marketing/pages/DocumentationPage'));

// Finance layout and pages - lazy load
const FinanceLayout = React.lazy(() => import('./domains/finance/layouts/FinanceLayout'));
const FinanceOverview = React.lazy(() => import('./domains/finance/pages/FinanceOverview'));
const ProfitabilityPage = React.lazy(() => import('./domains/finance/pages/ProfitabilityPage'));
const BalanceSheetPage = React.lazy(() => import('./domains/finance/pages/BalanceSheetPage'));
const CashFlowPage = React.lazy(() => import('./domains/finance/pages/CashFlowPage'));
const WorkingCapitalPage = React.lazy(() => import('./domains/finance/pages/WorkingCapitalPage'));
const ProjectCostingPage = React.lazy(() => import('./domains/finance/pages/ProjectCostingPage'));
const DataModelPage = React.lazy(() => import('./domains/finance/pages/DataModelPage'));
const FinanceDataManagementPage = React.lazy(() => import('./domains/finance/pages/DataManagementPage'));

// Sales layout and pages - lazy load
const SalesLayout = React.lazy(() => import('./domains/sales/layouts/SalesLayout'));
const SalesDashboard = React.lazy(() => import('./domains/sales/pages/SalesDashboard'));
const SalesFactBrowserPage = React.lazy(() => import('./domains/sales/pages/FactTableBrowserPage'));
const SalesDataModelPage = React.lazy(() => import('./domains/sales/pages/DataModelPage'));
const SalesDataManagementPage = React.lazy(() => import('./domains/sales/pages/DataManagementPage'));

// Manufacturing pages - lazy load
const ManufacturingDashboard = React.lazy(() => import('./domains/manufacturing/pages/ManufacturingDashboard'));

// Operations pages - lazy load
const OperationsDashboard = React.lazy(() => import('./domains/operations/pages/OperationsDashboard'));

// Logistics layout and pages - lazy load
const LogisticsLayout = React.lazy(() => import('./domains/logistics/layouts/LogisticsLayout'));
const DealerMapPage = React.lazy(() => import('./domains/logistics/pages/DealerMapPage'));
const DealerPipelinePage = React.lazy(() => import('./domains/logistics/pages/DealerPipelinePage'));
const LogisticsDataManagementPage = React.lazy(() => import('./domains/logistics/pages/DataManagementPage'));

// Procurement & Inventory layout and pages - lazy load
const ProcurementLayout = React.lazy(() => import('./domains/procurement/layouts/ProcurementLayout'));
const ProcurementInventoryPage = React.lazy(() => import('./domains/procurement/pages/InventoryPage'));
const ProcurementPurchaseOrdersPage = React.lazy(() => import('./domains/procurement/pages/PurchaseOrdersPage'));
const ProcurementProductionOrdersPage = React.lazy(() => import('./domains/procurement/pages/ProductionOrdersPage'));
const ProcurementDataManagementPage = React.lazy(() => import('./domains/procurement/pages/DataManagementPage'));

// Admin panel layout and page - lazy load
const AdminLayout = React.lazy(() => import('./pages/AdminLayout'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const DomainConfigPage = React.lazy(() => import('./pages/DomainConfigPage'));

// Widget Validator
const WidgetValidator = React.lazy(() => import('./pages/WidgetValidator'));

// Login page (new auth system)
const Login = React.lazy(() => import('./pages/Login'));

// Landing page
const LandingPage = React.lazy(() => import('./pages/LandingPage'));


function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BusinessEntityProvider>
        <React.Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Login - outside MainLayout */}
            <Route path="/login" element={<Login />} />

            <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            {/* Default route - Landing Page */}
            <Route index element={<LandingPage />} />

            {/* Admin - Domain Configuration */}
            <Route path="admin/domain-config" element={<DomainConfigPage />} />

            {/* Widget Validator */}
            <Route path="admin/widget-validator" element={<WidgetValidator />} />

            {/* Marketing Domain */}
            <Route path="marketing" element={<DomainProtectedRoute domain="marketing"><MarketingLayout /></DomainProtectedRoute>}>
              <Route index element={<DomainIndexRedirect domainKey="marketing" fallbackPath="/marketing/overview" />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="performance" element={<MarketingPerformancePage />} />
              <Route path="campaigns" element={<CampaignsPage />} />
              <Route path="web-traffic" element={<WebTrafficPage />} />
              <Route path="market" element={<MarketPage />} />
              <Route path="competitors" element={<CompetitorsPage />} />
              <Route path="trends" element={<TrendsPage />} />
              <Route path="partnerships" element={<PartnershipsPage />} />
              <Route path="online-reviews" element={<OnlineReviewsPage />} />
              <Route path="advertising-performance" element={<AdvertisingPerformancePage />} />
              <Route path="market-share" element={<MarketSharePage />} />
              <Route path="completion-guarantees" element={<CompletionGuaranteesPage />} />
              <Route path="data-model" element={<MarketingDataModel />} />
              <Route path="data-management" element={<MarketingDataManagementPage />} />
              <Route path="upload-management" element={<UploadManagementPage />} />
              <Route path="documentation" element={<MarketingDocumentationPage />} />
            </Route>

            {/* Finance Domain */}
            <Route path="finance" element={<DomainProtectedRoute domain="finance"><FinanceLayout /></DomainProtectedRoute>}>
              <Route index element={<DomainIndexRedirect domainKey="finance" fallbackPath="/finance/overview" />} />
              <Route path="overview" element={<FinanceOverview />} />
              <Route path="profitability" element={<ProfitabilityPage />} />
              <Route path="balance-sheet" element={<BalanceSheetPage />} />
              <Route path="cash-flow" element={<CashFlowPage />} />
              <Route path="working-capital" element={<WorkingCapitalPage />} />
              <Route path="project-costing" element={<ProjectCostingPage />} />
              <Route path="data-model" element={<DataModelPage />} />
              <Route path="data-management" element={<FinanceDataManagementPage />} />
            </Route>

            {/* Sales Domain */}
            <Route path="sales" element={<DomainProtectedRoute domain="sales"><SalesLayout /></DomainProtectedRoute>}>
              <Route index element={<DomainIndexRedirect domainKey="sales" fallbackPath="/sales/dashboard" />} />
              <Route path="dashboard" element={<SalesDashboard />} />
              <Route path="fact-browser" element={<SalesFactBrowserPage />} />
              <Route path="data-model" element={<SalesDataModelPage />} />
              <Route path="data-management" element={<SalesDataManagementPage />} />
            </Route>

            {/* Manufacturing Domain */}
            <Route path="manufacturing" element={<DomainProtectedRoute domain="manufacturing"><ManufacturingDashboard /></DomainProtectedRoute>} />
            <Route path="manufacturing/:tab" element={<DomainProtectedRoute domain="manufacturing"><ManufacturingDashboard /></DomainProtectedRoute>} />

            {/* Operations Domain */}
            <Route path="operations" element={<DomainProtectedRoute domain="operations"><OperationsDashboard /></DomainProtectedRoute>} />
            <Route path="operations/:tab" element={<DomainProtectedRoute domain="operations"><OperationsDashboard /></DomainProtectedRoute>} />

            {/* Logistics Domain */}
            <Route path="logistics" element={<DomainProtectedRoute domain="logistics"><LogisticsLayout /></DomainProtectedRoute>}>
              <Route index element={<Navigate to="dealer-map" replace />} />
              <Route path="dealer-map" element={<DealerMapPage />} />
              <Route path="pipeline" element={<DealerPipelinePage />} />
              <Route path="data-management" element={<LogisticsDataManagementPage />} />
            </Route>

            {/* Procurement & Inventory Domain */}
            <Route path="procurement" element={<DomainProtectedRoute domain="procurement"><ProcurementLayout /></DomainProtectedRoute>}>
              <Route index element={<DomainIndexRedirect domainKey="procurement" fallbackPath="/procurement/inventory" />} />
              <Route path="inventory" element={<ProcurementInventoryPage />} />
              <Route path="purchase-orders" element={<ProcurementPurchaseOrdersPage />} />
              <Route path="production-orders" element={<ProcurementProductionOrdersPage />} />
              <Route path="data-management" element={<ProcurementDataManagementPage />} />
            </Route>

            {/* Admin Panel - User, Role & Permission Management */}
            <Route path="admin-panel" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin-panel/users" replace />} />
              <Route path="users" element={<AdminPanel />} />
              <Route path="roles" element={<AdminPanel />} />
              <Route path="permissions" element={<AdminPanel />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </React.Suspense>
        </BusinessEntityProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    flexDirection: 'column'
  }}>
    <div style={{
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #1e3a5f',
      borderRadius: '50%',
      width: '50px',
      height: '50px',
      animation: 'spin 1s linear infinite'
    }} />
    <p style={{ marginTop: '20px', color: '#666' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

export default App;
